// Special orders (a part or item ordered for a customer) and package tracking links.

export const ORDER_STATUSES = ['ordered', 'shipped', 'arrived', 'picked_up', 'cancelled'] as const;
export type OrderStatus = (typeof ORDER_STATUSES)[number];
export const OPEN_ORDER_STATUSES: OrderStatus[] = ['ordered', 'shipped', 'arrived'];
export const ORDER_STATUS_LABELS: Record<OrderStatus, string> = {
  ordered: 'Ordered',
  shipped: 'On the way',
  arrived: 'Arrived: ready for pickup',
  picked_up: 'Picked up',
  cancelled: 'Cancelled',
};

export function isOrderStatus(v: unknown): v is OrderStatus {
  return typeof v === 'string' && (ORDER_STATUSES as readonly string[]).includes(v);
}

/** O-1001, O-1002, ... */
export function orderNumber(n: number): string {
  return `O-${n}`;
}

export function parseOrderNumber(q: string): number | null {
  const m = /^o[-\s]?0*(\d{1,7})$/i.exec(q.trim());
  return m ? Number(m[1]) : null;
}

export type ShipCarrier = 'ups' | 'fedex' | 'usps' | 'other';
export const SHIP_CARRIER_LABELS: Record<ShipCarrier, string> = { ups: 'UPS', fedex: 'FedEx', usps: 'USPS', other: 'Other' };

/** Guesses the carrier from a tracking number's shape. */
export function guessCarrier(tracking: string): ShipCarrier | null {
  const t = tracking.replace(/\s+/g, '').toUpperCase();
  if (!t) return null;
  if (/^1Z[0-9A-Z]{16}$/.test(t)) return 'ups';
  if (/^(94|93|92|95|82)\d{18,20}$/.test(t) || /^[A-Z]{2}\d{9}US$/.test(t)) return 'usps';
  if (/^\d{12}$|^\d{15}$|^\d{20}$|^\d{22}$/.test(t)) return 'fedex';
  return null;
}

/** The carrier's public tracking page. */
export function trackingUrl(carrier: string | null, tracking: string | null): string | null {
  const t = (tracking ?? '').replace(/\s+/g, '');
  if (!t) return null;
  switch (carrier) {
    case 'ups': return `https://www.ups.com/track?tracknum=${encodeURIComponent(t)}`;
    case 'fedex': return `https://www.fedex.com/fedextrack/?trknbr=${encodeURIComponent(t)}`;
    case 'usps': return `https://tools.usps.com/go/TrackConfirmAction?tLabels=${encodeURIComponent(t)}`;
    default: return null;
  }
}

/** Carriers for bill payments (VidaPay covers most prepaid brands). */
export const PHONE_CARRIERS = ['T-Mobile', 'Metro by T-Mobile', 'AT&T', 'Cricket', 'Verizon', 'Boost', 'Simple Mobile', 'Ultra Mobile', 'Lyca', 'H2O', 'Tracfone', 'Mint', 'Other'];

/** The only carriers the store activates lines for. */
export const ACTIVATION_CARRIERS = ['T-Mobile', 'AT&T', 'Verizon', 'Tracfone', 'Simple Mobile'] as const;
export type ActivationCarrier = (typeof ACTIVATION_CARRIERS)[number];
export function isActivationCarrier(v: unknown): v is ActivationCarrier {
  return typeof v === 'string' && (ACTIVATION_CARRIERS as readonly string[]).includes(v);
}

/** Every activation type ever rung up (older receipts and reports still name the old ones). */
export const ACTIVATION_TYPES: Record<string, string> = { new_line: 'New Line', port_in: 'Port-In', upgrade: 'Upgrade', prepaid: 'Prepaid plan / refill' };
/** What the activation screen offers: a new line, or a port-in (the customer keeps their number). */
export const ACTIVATION_TYPES_OFFERED = ['new_line', 'port_in'] as const;

/**
 * What a carrier pays the store for each activation ("T-Mobile | 25", one carrier per line). A
 * carrier with no amount has no rate yet: its activations are counted and wait for one. These are
 * the business's commissions: never shown to sales staff, never on the customer's bill.
 */
export const DEFAULT_ACTIVATION_COMMISSIONS = 'T-Mobile | 25\nAT&T | 35\nVerizon | 45\nTracfone |\nSimple Mobile |';

/** The rates in cents by carrier (null = not set yet), or null if the text isn't written right. */
export function parseCommissionRates(text: string): Map<ActivationCarrier, number | null> | null {
  const out = new Map<ActivationCarrier, number | null>(ACTIVATION_CARRIERS.map((c) => [c, null]));
  for (const raw of text.split('\n')) {
    const line = raw.trim();
    if (!line) continue;
    const m = /^([^|]+?)\s*\|\s*(?:\$?\s*(\d{1,4}(?:\.\d{1,2})?))?\s*$/.exec(line);
    if (!m || !isActivationCarrier(m[1].trim())) return null;
    out.set(m[1].trim() as ActivationCarrier, m[2] === undefined ? null : Math.round(Number(m[2]) * 100));
  }
  return out;
}

/** The rates back as settings text, every carrier on its own line. */
export function commissionRatesText(rates: Map<ActivationCarrier, number | null>): string {
  return ACTIVATION_CARRIERS.map((c) => {
    const v = rates.get(c);
    return `${c} |${v === null || v === undefined ? '' : ` ${(v / 100).toFixed(2).replace(/\.00$/, '')}`}`;
  }).join('\n');
}
